import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { ArrowRight, HeartHandshake, TriangleAlert } from 'lucide-react';
import { Card } from '@/components/common/Card';
import { PageHeader } from '@/components/layout/PageHeader';
import { ErrorState } from '@/components/common/States';
import { Spinner } from '@/components/common/Spinner';
import { hrReportsApi } from '@/services/api/endpoints/hr';
import { queryKeys } from '@/services/api/queryKeys';
import { ROUTES } from '@/app/router/routes';
import { useAuth } from '@/features/auth/useAuth';
import { PERMISSIONS } from '@/types/permissions';
import { formatDate } from '@/utils/format';
import { BarRow, Money, PayrollStatusBadge, Stat, humanise } from './shared';

/**
 * Where HR stands today.
 *
 * <p>Every figure here is one the database can state outright — a count, a sum,
 * a status. Nothing is projected or averaged. A dashboard that shows a
 * plausible-looking number nobody can trace back is worse than one that shows
 * fewer numbers, and this system has already been bitten once by a placeholder
 * that shipped returning zero.
 */
export function HrDashboardPage() {
  const { can } = useAuth();
  const seesPayroll = can(PERMISSIONS.PAYROLL_PERIOD_VIEW);

  const dashboard = useQuery({
    queryKey: queryKeys.hr.dashboard,
    queryFn: () => hrReportsApi.dashboard(),
  });

  if (dashboard.isError) {
    return <ErrorState error={dashboard.error} onRetry={() => void dashboard.refetch()} />;
  }
  if (dashboard.isPending) {
    return (
      <div className="flex justify-center py-16">
        <Spinner />
      </div>
    );
  }

  const data = dashboard.data;
  const maxDepartment = Math.max(1, ...data.byDepartment.map((row) => row.count));

  return (
    <div>
      <PageHeader
        title="HR Dashboard"
        description="Headcount, absence and payroll at a glance."
        icon={<HeartHandshake className="h-5 w-5" aria-hidden="true" />}
      />

      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <Stat label="Headcount" value={data.headcount} hint="In a paid employment status" />
        <Stat
          label="On leave today"
          value={data.onLeaveToday}
          tone={data.onLeaveToday > 0 ? 'warning' : 'neutral'}
          hint="Approved leave covering today"
        />
        <Stat
          label="Leave awaiting a decision"
          value={data.leaveAwaitingDecision}
          tone={data.leaveAwaitingDecision > 0 ? 'warning' : 'neutral'}
          hint={
            data.leaveAwaitingDecision > 0 ? (
              <Link to={ROUTES.leaveRequests} className="text-accent underline-offset-2 hover:underline">
                Review them
              </Link>
            ) : (
              'Nothing waiting'
            )
          }
        />
        {seesPayroll ? (
          <Stat
            label="Staff loans outstanding"
            value={<Money value={data.loansOutstanding} />}
            hint={`${data.activeLoans} loan${data.activeLoans === 1 ? '' : 's'} being recovered`}
          />
        ) : (
          <Stat label="Departments" value={data.byDepartment.length} hint="With staff assigned" />
        )}
      </div>

      {/*
        Not an error, and not hidden either: an employee with no salary package
        is skipped silently by a payroll run, which is precisely the kind of
        thing that is noticed on payday rather than before it.
      */}
      {seesPayroll && data.withoutSalaryPackage > 0 && (
        <Card className="mt-3 border-l-4 border-l-[color:var(--sms-warning)] p-4">
          <div className="flex items-start gap-3">
            <TriangleAlert
              className="mt-0.5 h-5 w-5 shrink-0 text-[color:var(--sms-warning)]"
              aria-hidden="true"
            />
            <div>
              <p className="text-sm font-semibold text-ink">
                {data.withoutSalaryPackage} employee
                {data.withoutSalaryPackage === 1 ? ' has' : 's have'} no salary package
              </p>
              <p className="mt-0.5 text-[13px] text-muted">
                A payroll run will skip them. They are listed as skipped when you calculate, but it
                is cheaper to notice now.{' '}
                <Link
                  to={ROUTES.salaryAssignments}
                  className="text-accent underline-offset-2 hover:underline"
                >
                  Set their salaries
                </Link>
              </p>
            </div>
          </div>
        </Card>
      )}

      <div className="mt-3 grid gap-3 lg:grid-cols-3">
        <Card className="p-4 lg:col-span-2">
          <h2 className="text-sm font-extrabold uppercase tracking-[0.05em] text-ink">
            Headcount by department
          </h2>
          {data.byDepartment.length === 0 ? (
            <p className="mt-3 text-[13px] text-muted">Nobody is assigned to a department yet.</p>
          ) : (
            <div className="mt-2">
              {data.byDepartment.map((row) => (
                <BarRow key={row.label} label={row.label} value={row.count} max={maxDepartment} />
              ))}
            </div>
          )}
        </Card>

        <Card className="p-4">
          <h2 className="text-sm font-extrabold uppercase tracking-[0.05em] text-ink">
            By employment status
          </h2>
          <dl className="mt-2">
            {data.byStatus.map((row) => (
              <div
                key={row.label}
                className="flex items-baseline justify-between border-b border-line py-2 last:border-0"
              >
                <dt className="text-sm capitalize text-muted">{humanise(row.label)}</dt>
                <dd className="tabular-nums text-sm font-semibold text-ink">{row.count}</dd>
              </div>
            ))}
          </dl>
        </Card>
      </div>

      {seesPayroll && (
        <div className="mt-3 grid gap-3 lg:grid-cols-2">
          <Card className="p-4">
            <h2 className="text-sm font-extrabold uppercase tracking-[0.05em] text-ink">
              Payroll in progress
            </h2>
            {data.openPayroll === undefined || data.openPayroll === null ? (
              <p className="mt-3 text-[13px] text-muted">
                No run is open.{' '}
                <Link to={ROUTES.payroll} className="text-accent underline-offset-2 hover:underline">
                  Start one
                </Link>
              </p>
            ) : (
              <Link
                to={ROUTES.payrollDetail(data.openPayroll.id)}
                className="mt-3 flex items-center justify-between gap-3 border border-line p-3 transition-colors hover:bg-surface"
              >
                <span>
                  <span className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-ink">{data.openPayroll.name}</span>
                    <PayrollStatusBadge status={data.openPayroll.status} />
                  </span>
                  <span className="mt-0.5 block text-xs text-muted">
                    {data.openPayroll.employeeCount} payslip
                    {data.openPayroll.employeeCount === 1 ? '' : 's'} · pay on{' '}
                    {formatDate(data.openPayroll.paymentDate)}
                  </span>
                </span>
                <span className="flex shrink-0 items-center gap-2">
                  <Money value={data.openPayroll.totalNet} bold />
                  <ArrowRight className="h-4 w-4 text-muted" aria-hidden="true" />
                </span>
              </Link>
            )}
          </Card>

          <Card className="p-4">
            <h2 className="text-sm font-extrabold uppercase tracking-[0.05em] text-ink">
              Last run paid
            </h2>
            {data.lastPaidPayroll === undefined || data.lastPaidPayroll === null ? (
              <p className="mt-3 text-[13px] text-muted">Nothing has been paid yet.</p>
            ) : (
              <Link
                to={ROUTES.payrollDetail(data.lastPaidPayroll.id)}
                className="mt-3 flex items-center justify-between gap-3 border border-line p-3 transition-colors hover:bg-surface"
              >
                <span>
                  <span className="block text-sm font-semibold text-ink">
                    {data.lastPaidPayroll.name}
                  </span>
                  <span className="mt-0.5 block text-xs text-muted">
                    {data.lastPaidPayroll.employeeCount} paid on{' '}
                    {formatDate(data.lastPaidPayroll.paymentDate)} · gross{' '}
                    <Money value={data.lastPaidPayroll.totalGross} />
                  </span>
                </span>
                <span className="flex shrink-0 items-center gap-2">
                  <Money value={data.lastPaidPayroll.totalNet} bold />
                  <ArrowRight className="h-4 w-4 text-muted" aria-hidden="true" />
                </span>
              </Link>
            )}
          </Card>
        </div>
      )}

      <div className="mt-3 grid gap-3 lg:grid-cols-2">
        <MovementCard
          title="Starting in the next 30 days"
          empty="Nobody is due to start."
          rows={data.upcomingJoiners}
        />
        <MovementCard
          title="Leaving in the next 30 days"
          empty="Nobody is due to leave."
          rows={data.upcomingLeavers}
        />
      </div>
    </div>
  );
}

function MovementCard({
  title,
  empty,
  rows,
}: {
  title: string;
  empty: string;
  rows: { employeeId: string; name: string; employeeNumber?: string; date: string }[];
}) {
  return (
    <Card className="p-4">
      <h2 className="text-sm font-extrabold uppercase tracking-[0.05em] text-ink">{title}</h2>
      {rows.length === 0 ? (
        <p className="mt-3 text-[13px] text-muted">{empty}</p>
      ) : (
        <ul className="mt-2">
          {rows.map((row) => (
            <li
              key={row.employeeId}
              className="flex items-baseline justify-between gap-3 border-b border-line py-2 last:border-0"
            >
              <span className="min-w-0">
                <span className="block truncate text-sm text-ink">{row.name}</span>
                <span className="block font-mono text-[11px] text-subtle">
                  {row.employeeNumber}
                </span>
              </span>
              <span className="shrink-0 text-xs text-muted">{formatDate(row.date)}</span>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}
